// ================================================
// ACTION ITEM TRACKER PANEL
// Feature 3 - Track commitments with status and source
// ================================================
import React, { useState, useMemo } from 'react';
import { 
  Plus, Send, Filter, Download, AlertTriangle, Clock,
  CheckCircle2, Circle, ChevronDown, Search, X
} from 'lucide-react';
import { CHIEF_OF_STAFF_COLORS } from '../focus';
import { actionItems, executiveTeam, getDaysUntil, formatDate } from '../../data/chiefOfStaffData';

const ActionItemsPanel = () => {
  const [items, setItems] = useState(actionItems);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ description: '', assigneeId: '', dueDate: '' });
  const [showFilters, setShowFilters] = useState(false);

  // Group items by status
  const groupedItems = useMemo(() => {
    let filtered = items;
    
    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.assignee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.source.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply filter
    if (filter !== 'all') {
      filtered = filtered.filter(item => item.status === filter);
    }
    
    return {
      overdue: filtered.filter(i => i.status === 'overdue'),
      due_today: filtered.filter(i => i.status === 'due_today'),
      in_progress: filtered.filter(i => i.status === 'in_progress'),
      not_started: filtered.filter(i => i.status === 'not_started')
    };
  }, [items, filter, searchQuery]);

  // Stats
  const stats = {
    total: items.filter(i => i.status !== 'completed').length,
    overdue: items.filter(i => i.status === 'overdue').length,
    dueToday: items.filter(i => i.status === 'due_today').length
  };

  const handleAddItem = () => {
    if (!newItem.description || !newItem.assigneeId) return;
    
    const assignee = executiveTeam.find(m => m.id === newItem.assigneeId);
    const newActionItem = {
      id: `ai-${Date.now()}`,
      description: newItem.description,
      assignee: assignee,
      assignedDate: new Date(),
      dueDate: newItem.dueDate ? new Date(newItem.dueDate) : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      status: 'not_started',
      source: 'Added by Chief of Staff',
      priority: 'medium'
    };
    
    setItems([newActionItem, ...items]);
    setNewItem({ description: '', assigneeId: '', dueDate: '' });
    setShowAddForm(false);
  };

  const handleFollowUp = (item) => {
    const message = `Hi ${item.assignee.name.split(' ')[0]},\n\nFollowing up on the action item: "${item.description}"\n\nThis was committed on ${formatDate(item.assignedDate)} with a due date of ${formatDate(item.dueDate)}.\n\nCould you please provide a status update?\n\nThank you`;
    alert(`Follow-up message:\n\n${message}`);
  };

  const handleExport = () => {
    alert('This would generate a formatted action item report for the executive');
  };

  const statusConfig = {
    overdue: { label: 'Overdue', color: '#EF4444', bgColor: '#EF444420' },
    due_today: { label: 'Due Today', color: '#F59E0B', bgColor: '#F59E0B20' },
    in_progress: { label: 'In Progress', color: CHIEF_OF_STAFF_COLORS.primary, bgColor: CHIEF_OF_STAFF_COLORS.primary + '20' },
    not_started: { label: 'Upcoming', color: '#6B7280', bgColor: '#6B728020' }
  };

  return (
    <div className="p-5 space-y-4">
      {/* Header Stats */}
      <div 
        className="flex items-center justify-between p-4 rounded-xl"
        style={{ 
          backgroundColor: stats.overdue > 0 ? '#EF444410' : '#161B22',
          border: `1px solid ${stats.overdue > 0 ? '#EF444430' : CHIEF_OF_STAFF_COLORS.primary + '20'}`
        }}
      >
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-xs text-gray-400">Open Items</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-500">{stats.overdue}</p>
            <p className="text-xs text-gray-400">Overdue</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-amber-500">{stats.dueToday}</p>
            <p className="text-xs text-gray-400">Due Today</p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      {/* Quick Add */}
      {showAddForm ? (
        <div 
          className="p-4 rounded-xl space-y-3"
          style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}30` }}
        >
          <input
            type="text"
            placeholder="Action item description..."
            value={newItem.description}
            onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
            className="w-full p-3 rounded-lg text-sm text-white placeholder-gray-500"
            style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={newItem.assigneeId}
              onChange={(e) => setNewItem({ ...newItem, assigneeId: e.target.value })}
              className="p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
            >
              <option value="">Select assignee...</option>
              {executiveTeam.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
            <input
              type="date"
              value={newItem.dueDate}
              onChange={(e) => setNewItem({ ...newItem, dueDate: e.target.value })}
              className="p-3 rounded-lg text-sm text-white"
              style={{ backgroundColor: '#0D1117', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddItem}
              className="flex-1 py-2 rounded-lg text-sm font-medium"
              style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
            >
              Add Item
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-white/10 text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all"
          style={{ backgroundColor: '#161B22', border: `1px dashed ${CHIEF_OF_STAFF_COLORS.primary}40`, color: CHIEF_OF_STAFF_COLORS.primary }}
        >
          <Plus className="w-4 h-4" />
          Quick Add Action Item
        </button>
      )}

      {/* Search and Filter */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm text-white placeholder-gray-500"
            style={{ backgroundColor: '#161B22', border: `1px solid ${CHIEF_OF_STAFF_COLORS.primary}20` }}
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
            style={{ 
              backgroundColor: filter !== 'all' ? CHIEF_OF_STAFF_COLORS.primary : '#161B22',
              color: filter !== 'all' ? 'white' : '#9CA3AF'
            }}
          >
            <Filter className="w-4 h-4" />
            Filter
          </button>
          {showFilters && (
            <div 
              className="absolute right-0 top-full mt-2 w-48 rounded-lg overflow-hidden z-10"
              style={{ backgroundColor: '#1F2937' }}
            >
              {['all', 'overdue', 'due_today', 'in_progress', 'not_started'].map(status => (
                <button
                  key={status}
                  onClick={() => { setFilter(status); setShowFilters(false); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-all"
                  style={{ color: filter === status ? CHIEF_OF_STAFF_COLORS.primary : 'white' }}
                >
                  {status === 'all' ? 'All Items' : statusConfig[status]?.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Item Lists by Status */}
      {Object.entries(groupedItems).map(([status, statusItems]) => {
        if (statusItems.length === 0) return null;
        const config = statusConfig[status];
        
        return (
          <div key={status} className="space-y-2">
            <h3 
              className="text-xs font-bold uppercase tracking-wider flex items-center gap-2"
              style={{ color: config.color }}
            >
              {status === 'overdue' && <AlertTriangle className="w-3 h-3" />}
              {status === 'due_today' && <Clock className="w-3 h-3" />}
              {config.label} ({statusItems.length})
            </h3>
            
            {statusItems.map((item) => (
              <div 
                key={item.id}
                className="p-4 rounded-xl"
                style={{ 
                  backgroundColor: '#161B22',
                  borderLeft: `4px solid ${config.color}`
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-white mb-2">{item.description}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary }}
                        >
                          {item.assignee.avatar}
                        </div>
                        {item.assignee.name}
                      </span>
                      <span>•</span>
                      <span>{getDaysUntil(item.dueDate)}</span>
                      <span>•</span>
                      <span className="text-gray-500">{item.source}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleFollowUp(item)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{ backgroundColor: config.bgColor, color: config.color }}
                  >
                    <Send className="w-3 h-3" />
                    Follow Up
                  </button>
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default ActionItemsPanel;
